import { isBrowser } from '@/utils/environmentSafety';
import { LANGUAGES_VERSION } from '@/constants/languages';

const MAX_CACHE_ENTRIES = 500;
const BATCH_DELAY_MS = 150;
const MAX_BATCH_SIZE = 20;

// In-memory cache: "lang:englishText" → translated
const memoryCache = new Map<string, string>();

// Pending queue per language
const pendingQueues = new Map<string, Set<string>>();
const batchTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Listener for when translations arrive
let onTranslationsReady: (() => void) | null = null;

export function setTranslationListener(listener: () => void) {
  onTranslationsReady = listener;
}

function cacheKey(lang: string, text: string): string {
  return `${lang}:${text}`;
}

function getStorageKey(lang: string): string {
  return `behtari_ai_translations_${lang}`;
}

// Load from localStorage into memory on init
function loadFromStorage(lang: string): void {
  if (!isBrowser) return;
  try {
    const storedVersion = localStorage.getItem('behtari_ai_trans_version');
    if (storedVersion !== LANGUAGES_VERSION) {
      // Clear stale caches on version change
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('behtari_ai_translations_')) {
          localStorage.removeItem(key);
        }
      }
      localStorage.setItem('behtari_ai_trans_version', LANGUAGES_VERSION);
      return;
    }

    const raw = localStorage.getItem(getStorageKey(lang));
    if (!raw) return;
    const entries: Record<string, string> = JSON.parse(raw);
    for (const [engText, translated] of Object.entries(entries)) {
      memoryCache.set(cacheKey(lang, engText), translated);
    }
  } catch { /* noop */ }
}

function saveToStorage(lang: string, newEntries: Record<string, string>): void {
  if (!isBrowser) return;
  try {
    const raw = localStorage.getItem(getStorageKey(lang));
    let existing: Record<string, string> = {};
    if (raw) {
      existing = JSON.parse(raw);
    }
    Object.assign(existing, newEntries);

    // Enforce max entries
    const keys = Object.keys(existing);
    if (keys.length > MAX_CACHE_ENTRIES) {
      const toRemove = keys.slice(0, keys.length - MAX_CACHE_ENTRIES);
      for (const k of toRemove) delete existing[k];
    }

    localStorage.setItem(getStorageKey(lang), JSON.stringify(existing));
  } catch { /* noop */ }
}

const loadedLanguages = new Set<string>();

export function getCachedTranslation(lang: string, englishText: string): string | undefined {
  if (!loadedLanguages.has(lang)) {
    loadFromStorage(lang);
    loadedLanguages.add(lang);
  }
  return memoryCache.get(cacheKey(lang, englishText));
}

export function queueForTranslation(lang: string, englishText: string): void {
  if (lang === 'en' || !isBrowser) return;
  
  // Skip if already cached or already queued
  if (memoryCache.has(cacheKey(lang, englishText))) return;

  let queue = pendingQueues.get(lang);
  if (!queue) {
    queue = new Set();
    pendingQueues.set(lang, queue);
  }
  queue.add(englishText);

  // Debounce: reset timer
  const existingTimer = batchTimers.get(lang);
  if (existingTimer) clearTimeout(existingTimer);

  batchTimers.set(lang, setTimeout(() => {
    flushQueue(lang);
  }, BATCH_DELAY_MS));
}

async function flushQueue(lang: string): Promise<void> {
  const queue = pendingQueues.get(lang);
  if (!queue || queue.size === 0) return;

  const texts = Array.from(queue).slice(0, MAX_BATCH_SIZE);
  queue.clear();
  batchTimers.delete(lang);

  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      // AI translation requires an authenticated user; skip silently for logged-out visitors.
      return;
    }
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    const response = await fetch(`${supabaseUrl}/functions/v1/ai-translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ texts, targetLanguage: lang }),
    });

    if (!response.ok) {
      if (response.status === 402) {
        console.warn('AI translation: credits exhausted, skipping');
      } else {
        console.warn('AI translation request failed:', response.status);
      }
      return;
    }

    const data = await response.json();
    if (!data.translations || data.translations.length !== texts.length) return;

    const newEntries: Record<string, string> = {};
    for (let i = 0; i < texts.length; i++) {
      const key = cacheKey(lang, texts[i]);
      memoryCache.set(key, data.translations[i]);
      newEntries[texts[i]] = data.translations[i];
    }

    saveToStorage(lang, newEntries);

    // Notify context to re-render
    onTranslationsReady?.();
  } catch (err) {
    console.warn('AI translation error:', err);
  }
}
